import React, { createContext, useContext, useState, useEffect } from "react";
import { Cnt } from "../Cnt.ThreeDeeCrate.SDK/Cnt.ThreeDeeCrate.SDK";
import { Cnt as PublicCnt } from "../Cnt.ThreeDeeCrate.Public.SDK/Cnt.ThreeDeeCrate.Public.SDK";
import { AppSettings } from "../AppSettings";

interface UserContextType {
  user: Cnt.ThreeDeeCrate.SDK.UserDTO | null;
  setUser: (user: Cnt.ThreeDeeCrate.SDK.UserDTO | null, token?: string) => void;
  logout: () => Promise<void>;
  loading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<Cnt.ThreeDeeCrate.SDK.UserDTO | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setUserState(null);
        } else {
          const api = new Cnt.ThreeDeeCrate.SDK.Api(AppSettings.APIUrl);
          const publicApi = new PublicCnt.ThreeDeeCrate.SDK.Api(AppSettings.PublicAPIUrl);

          const authResultWithNoIdAndWithSubscription =
            await publicApi.auth.ValidateUserSubscription();

          if (authResultWithNoIdAndWithSubscription?.Token) {
            localStorage.setItem("access_token", authResultWithNoIdAndWithSubscription?.Token ?? "");
            const authResultWithIdAndWithSubscription = await api.auth.SynchronizeUser();
            setUser(
              authResultWithIdAndWithSubscription?.User ?? null,
              authResultWithIdAndWithSubscription.Token
            );
          } else {
            localStorage.removeItem("access_token");
            setUser(null, "");
            console.error("🔴 Failed to authenticate user.");
          }
        }
      } catch (err) {
        console.error("Error fetching user", err);
        setUserState(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const setUser = (user: Cnt.ThreeDeeCrate.SDK.UserDTO | null, token?: string) => {
    setUserState(user);
    if (user && token) {
      localStorage.setItem("access_token", token);
    } else {
      localStorage.removeItem("access_token");
    }
  };

  const logout = async () => {
    localStorage.removeItem("access_token");
    setUserState(null);
  };

  return (
  <UserContext.Provider value={{ user, setUser, logout, loading }}>
    {loading ? (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          // background: "#f9f9f9"
        }}
      >
        <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }} />
        <span style={{ marginTop: "1rem", fontSize: "1.1rem" }} className={"text-warning"}>
          Authenticating user...
        </span>
      </div>
    ) : (
      children
    )}
  </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used inside UserProvider");
  return context;
};
